﻿import { createElement, useEffect, useState } from "react";
import { QuestionnaireSelectableItem } from "../LegalEntityAudit.types";
import { SelectableItem } from "../../../processingActivity/ProcessingActivity.types";
import { LegalEntityContactRow } from "../../legalEntityContactTab/LegalEntityContactTab.types";
import { post } from "../../../common/api/apiShared";
import { LegalEntityAuditSaveModel, SendAuditViewModel } from "./SendAuditDialog.types";
import { ISendAuditDialogProps } from "./SendAuditDialog";
import { Trans } from "react-i18next";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useValidator } from "../../../common/hooks/validation";
import { useTranslation } from "../../../localization/useTranslation";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { useResponsibleQuery } from "../../../user/hooks/useResponsibleQuery";
import { UserSelectableItem } from "../../../user/User.types";
import { useGroupEntitiesQuery } from "../../../processingActivity/hooks/useGroupEntitiesQuery.ts";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { apiUrls } from "@common/api/apiUrls";
import { getAdditionalRecipientOptions, withoutContact } from "../auditAdditionalRecipients.utils";

export function useSendAuditDialog(props: ISendAuditDialogProps) {
    const { translateString } = useTranslation();
    const snackBar = useDotLegalSnackbar();
    const isCopy = props.questionnaire !== undefined;

    const { gdpo, customerName } = useUserContext();

    const groupEntitiesQuery = useGroupEntitiesQuery();
    let responsibleQuery = useResponsibleQuery(true);

    let selectableGroupEntitiesData = groupEntitiesQuery.allGroupEntities;
    let selectableGroupEntitiesLoading = groupEntitiesQuery.isLoading;

    let selectableResponsiblesData: Array<UserSelectableItem> = [];
    if (selectableGroupEntitiesData) {
        selectableResponsiblesData = responsibleQuery.data(undefined) ?? [];
    }

    let selectableresponsiblesLoading = responsibleQuery.isLoading;

    // Same url as the contact select, so both fields share one request
    const contactsQuery = useDotLegalQuery<Array<LegalEntityContactRow>>(apiUrls().legalEntities.contacts(props.legalEntityId!));
    const contacts = contactsQuery.data ?? [];

    useEffect(() => {
        if (selectableGroupEntitiesData && selectableGroupEntitiesData.length === 1 && !gdpo) {
            onGroupEntityChanged([selectableGroupEntitiesData[0].id]);
        } else if (gdpo) {
            onGroupEntityChanged([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectableGroupEntitiesData]);

    const [step, setStep] = useState<"first" | "selectGroupEntity" | "send">("first");

    const [model, setModel] = useState<SendAuditViewModel>({
        legalEntityId: props.legalEntityId,
        legalEntityName: props.legalEntityName,
        additionalRecipientIds: [],
        systemIds: [],
        systemNames: [],
        deadline: null,
        groupEntityId: undefined,
        groupEntityName: "",
        questionnaireId: props.questionnaire?.id,
        questionnaireName: props.questionnaire?.name,
        processingActivities: [],
    });

    const validation = useValidator<SendAuditViewModel>(
        (validators) => ({
            questionnaireId: validators.validateNotEmpty((item) => item.questionnaireId, translateString("audit")),
            contactId: validators.validateNotEmpty((item) => item.contactId, translateString("contact")),
            deadline: validators.validateTodayOrInTheFuture((item) => item.deadline, translateString("deadline")),
            groupEntityId: validators.validateNotEmpty(
                (item) => item.groupEntityId,
                "",
                () => gdpo || step !== "selectGroupEntity",
                translateString("oneSenderIsRequired")
            ),
            responsibleId: validators.validateNotEmpty((item) => item.responsibleId, translateString("responsible")),
        }),
        [step]
    );

    const sendMutation = useDotLegalMutation(sendUrl, {
        onSuccess: (response: any) => {
            snackBar.show(
                createElement(Trans, {
                    i18nKey: "legalEntityAuditSentMessage",
                    values: { questionnaireName: model.questionnaireName, contactName: model.contactName },
                })
            );
            props.onAuditSent();
        },
    });

    const copyMutation = useDotLegalMutation(copyUrl, {
        onSuccess: (response: any) => {
            if (response.success()) {
                snackBar.show(
                    createElement(Trans, {
                        i18nKey: "legalEntityAuditSentMessage",
                        values: { questionnaireName: model.questionnaireName, contactName: model.contactName },
                    })
                );
            } else {
                snackBar.show(
                    createElement(Trans, {
                        i18nKey: "legalEntityAuditCopyFailed",
                        values: { questionnaireName: model.questionnaireName, contactName: model.contactName },
                    }),
                    "warning"
                );
            }
            props.onAuditSent();
        },
    });

    const onGroupEntityChanged = (groupEntityIds: Array<string>) => {
        const newModel = { ...model! };

        if (groupEntityIds.length === 0) {
            newModel.groupEntityId = undefined;
            newModel.groupEntityName = customerName;
        } else if (groupEntityIds.length === 1) {
            newModel.groupEntityId = groupEntityIds[0];
            newModel.groupEntityName = selectableGroupEntitiesData!.find((x) => x.id === groupEntityIds[0])!.name;
        } else {
            newModel.groupEntityId = groupEntityIds[1];
            newModel.groupEntityName = selectableGroupEntitiesData!.find((x) => x.id === groupEntityIds[1])!.name;
        }

        setModel(newModel);
    };

    const onUpdate = {
        onAuditChanged: (questionnaire: QuestionnaireSelectableItem | undefined) => {
            const newModel = { ...model! };
            newModel.questionnaireId = questionnaire?.id;
            newModel.questionnaireName = questionnaire?.name;
            setModel(newModel);
        },
        onContactChanged: (contact?: LegalEntityContactRow) => {
            const newModel = { ...model! };
            newModel.contactId = contact?.id;
            newModel.contactName = contact?.name;
            newModel.contactEmail = contact?.email;
            newModel.additionalRecipientIds = withoutContact(model.additionalRecipientIds, contact?.id);
            setModel(newModel);
        },
        onAdditionalRecipientsChanged: (additionalRecipientIds: Array<string>) => {
            setModel({ ...model, additionalRecipientIds });
        },
        onDateChanged: (date: Date | null) => {
            const newModel = { ...model! };
            newModel.deadline = date!;
            setModel(newModel);
        },
        onSystemsChanged: (systems: Array<SelectableItem>) => {
            const newModel = { ...model! };
            newModel.systemIds = systems.map((s) => s.id);
            newModel.systemNames = systems.map((s) => s.name);
            setModel(newModel);
        },
        onResponsibleChanged: (responsible: string | null) => {
            const newModel = { ...model! };
            newModel.responsibleId = responsible ?? undefined;
            setModel(newModel);
        },
        onProcessingActivityChange: (processingActivites: Array<SelectableItem>) => {
            const newModel = { ...model! };
            newModel.processingActivities = processingActivites;
            setModel(newModel);
        },
    };

    async function send() {
        if (isCopy) {
            await copyMutation.mutateAsync();
        } else {
            await sendMutation.mutateAsync();
        }
    }

    function copyUrl() {
        return post<{}>(`/legalEntityAudit/${props.responseId}/copy`, getSaveModel());
    }

    function sendUrl() {
        return post<{}>("/legalEntityAudit", getSaveModel());
    }

    function getSaveModel() {
        const saveModel: LegalEntityAuditSaveModel = {
            legalEntityId: model.legalEntityId!,
            questionnaireId: model.questionnaireId!,
            deadline: model.deadline!,
            contactId: model.contactId!,
            additionalRecipientIds: model.additionalRecipientIds,
            systemIds: model.systemIds,
            systemNames: model.systemNames,
            groupEntityId: model.groupEntityId,
            groupEntityName: model.groupEntityName,
            responsibleId: model.responsibleId!,
            processingActivityNames: model.processingActivities.map((x) => x.name),
            processingActivityIds: model.processingActivities.map((x) => x.id),
        };

        return saveModel;
    }

    const showSelectedGroupEntityStep = selectableGroupEntitiesData && selectableGroupEntitiesData.length > 1;

    const onNextOrSendClick = async () => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        if (step === "first") {
            if (showSelectedGroupEntityStep) {
                setStep("selectGroupEntity");
            } else {
                setStep("send");
            }
        } else if (step === "selectGroupEntity") {
            setStep("send");
        } else {
            await send();
        }
    };

    let onPreviousButtonClick = () => {
        if (step === "send") {
            if (showSelectedGroupEntityStep) {
                setStep("selectGroupEntity");
            } else {
                setStep("first");
            }
        } else {
            setStep("first");
        }
    };

    const additionalRecipientOptions = getAdditionalRecipientOptions(contacts, model.contactId, translateString);
    const additionalRecipientNames = contacts
        .filter((contact) => model.additionalRecipientIds.includes(contact.id))
        .map((contact) => contact.name)
        .join(", ");

    validation.check(model);

    return {
        onUpdate,
        onGroupEntityChanged,
        model,
        onNextOrSendClick,
        step,
        onPreviousButtonClick,
        isSending: sendMutation.isLoading || copyMutation.isLoading,
        validation,
        selectableGroupEntitiesLoading,
        selectableGroupEntitiesData,
        selectableResponsiblesData,
        selectableresponsiblesLoading,
        additionalRecipientOptions,
        additionalRecipientNames,
        isLoadingContacts: contactsQuery.isLoading,
    };
}
